import * as React from 'react';

/**
 * POZADI APLIKACE — PARCELY (krok GRAF1, zadani bod 2 a 3).
 *
 * Predloha `/srv/debata/maklerov-grafika-v3/VYSLEDEK.png` (tmavy rezim) a
 * `/srv/debata/maklerov-grafika-prehled-svetly/VYSLEDEK.png` (svetly): pod
 * sklenenymi panely lezi plocha znacky — dilky parcel ve TRECH SVISLYCH
 * PRUZICH, nahore vetsi, dole mensi, pres ne tenke katastralni linie.
 * PET dilku „sviti" (limetka), zbytek jsou jen obrysy.
 *
 * PROC KOMPONENTA A NE `background-image`:
 *  - tvary jsou SKUTECNE DILKY Z LOGA (`docs/design/brand-naves/logo/
 *    maklerov-naves-symbol-*.svg`, bajtove tytez cesty) — kreslit je znovu
 *    rucne by znamenalo kreslit do identity;
 *  - barvu kazde vrstvy urcuji TOKENY (`--rh-parcel-*`), takze rezim prepina
 *    tataz jedna sada promennych jako u zbytku aplikace. Data URI v CSS by
 *    muselo nest barvu natvrdo a v druhem rezimu by se nezmenilo — presne
 *    to, co zakazuje `no-hardcoded-colors.spec.ts`.
 *
 * STATICKE, BEZ ANIMACE: zadny `animation` ani `transition` (zadani:
 * „statické, bez animace náročné na CPU, respektuje `prefers-reduced-motion`").
 * Kdyz se nic nehybe, neni co pri `prefers-reduced-motion: reduce` vypinat —
 * pravidlo v `admin-v2.css` proto jen sklada dilky na jednu plochu bez
 * prolnuti. Hlida to test `parcel-backdrop.spec.tsx`.
 */

/**
 * Ctyri dilky znacky „Náves" — cesty jsou BAJTOVE TYTEZ jako v
 * `docs/design/brand-naves/logo/maklerov-naves-symbol-tmave.svg` (souradnice
 * 0–101). Shodu meri `parcel-backdrop.spec.tsx` proti souboru balicku, aby
 * se pozadi nemohlo rozejit s logem.
 */
export const PARCEL_PATHS: readonly string[] = [
  'M3 19L36 1Q39 -1 42 0L49 2Q54 3 51 10L35 49Q33 54 28 53L6 49Q2 49 2 44L0 26Q-1 22 3 19Z',
  'M62 8L94 14Q98 15 98 20L99 44Q99 49 94 47L53 34Q49 33 51 28L58 12Q59 7 62 8Z',
  'M8 59Q9 55 14 57L45 68Q50 70 48 75L40 96Q39 100 34 100H15Q11 100 10 96L2 69Q1 65 4 62Z',
  'M59 54Q60 49 65 51L95 56Q99 57 99 62L101 80Q102 85 97 87L61 100Q57 102 53 99Q50 97 52 92Z',
];

interface Tile {
  /** Index do `PARCEL_PATHS` — ktery dilek loga to je. */
  readonly shape: number;
  readonly x: number;
  readonly y: number;
  /** 1 = puvodni velikost dilku loga (~100 px). */
  readonly scale: number;
  readonly rotate: number;
  /** Sviti limetkou (zadani: presne pet dilku). */
  readonly lit?: true;
}

/**
 * Rozmisteni dilku. Zadne nahodne cislo: pozadi musi na snimku vypadat
 * pokazde stejne, jinak by zlata sada snimku byla k nicemu.
 *
 * Tri pruhy (x ~ 110 / 720 / 1320 ve viewBoxu 1440 × 900), v kazdem dilky
 * shora dolu se zmensujicim se `scale` (nahore vetsi, dole mensi — predloha).
 * Svitici dilky jsou PET a jsou rozlozene po vsech trech pruzich.
 */
/*
 * Krok GRAF4 nalez 6 — MERITKO DILKU. Do GRAF4 mel nejvetsi dilek `scale`
 * 2,6, tedy ~260 px: na snimku 1440 px z nej byl obri obrys pres pul karty,
 * zatimco predloha kresli dilky o STRANE 40–80 px, zato jich ma vic. Meritko
 * je proto 0,45–0,8 (nahore o stupen vetsi nez dole) a dilku je 21, aby pruhy
 * zustaly plne. Pocet svitcicich se NEMENI — pet, jak zada GRAF1.
 */
const TILES: readonly Tile[] = [
  // levy pruh
  { shape: 0, x: 60, y: 20, scale: 0.8, rotate: -8, lit: true },
  { shape: 3, x: 210, y: 150, scale: 0.7, rotate: 14 },
  { shape: 2, x: 40, y: 290, scale: 0.65, rotate: -18 },
  { shape: 1, x: 240, y: 400, scale: 0.6, rotate: 7 },
  { shape: 0, x: 90, y: 520, scale: 0.55, rotate: 22, lit: true },
  { shape: 2, x: 250, y: 650, scale: 0.5, rotate: -11 },
  { shape: 3, x: 70, y: 780, scale: 0.45, rotate: 16 },
  // stredni pruh
  { shape: 1, x: 620, y: 10, scale: 0.78, rotate: 10 },
  { shape: 2, x: 810, y: 130, scale: 0.7, rotate: -15, lit: true },
  { shape: 0, x: 600, y: 280, scale: 0.62, rotate: 18 },
  { shape: 3, x: 830, y: 410, scale: 0.58, rotate: -6 },
  { shape: 1, x: 640, y: 540, scale: 0.52, rotate: 16 },
  { shape: 0, x: 850, y: 670, scale: 0.48, rotate: -20 },
  { shape: 2, x: 660, y: 790, scale: 0.45, rotate: 9 },
  // pravy pruh
  { shape: 2, x: 1180, y: 20, scale: 0.8, rotate: 12 },
  { shape: 0, x: 1370, y: 140, scale: 0.72, rotate: -10 },
  { shape: 3, x: 1160, y: 300, scale: 0.64, rotate: 20, lit: true },
  { shape: 1, x: 1360, y: 420, scale: 0.58, rotate: -14 },
  { shape: 2, x: 1190, y: 550, scale: 0.52, rotate: 8, lit: true },
  { shape: 3, x: 1380, y: 680, scale: 0.48, rotate: -5 },
  { shape: 0, x: 1200, y: 800, scale: 0.45, rotate: 17 },
];

/** Pet svitcicich dilku — zadani bod 2 („limetkou svítí přesně 5 dílků"). */
export const LIT_TILE_COUNT = TILES.filter((tile) => tile.lit === true).length;

/** Katastralni linie — tenka sit pres celou plochu (kryti dava token). */
const CADASTRE_LINES: readonly string[] = [
  'M0 170L1440 110',
  'M0 430L1440 370',
  'M0 690L1440 650',
  'M330 0L270 900',
  'M980 0L1040 900',
];

/**
 * Vykresluje se JEDNOU v shellu (`admin-shell.tsx`) pod obsahem. `aria-hidden`
 * a `pointer-events: none` (CSS) — je to plocha, ne obsah.
 */
export function ParcelBackdrop(): React.JSX.Element {
  return (
    <div className="rh-a2-parcels" aria-hidden="true" data-testid="parcel-backdrop">
      <svg
        className="rh-a2-parcels__svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <g className="rh-a2-parcels__grid">
          {CADASTRE_LINES.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
        {TILES.map((tile, index) => (
          <path
            // Dilky se v seznamu opakuji (ctyri tvary, osmnact mist), takze
            // klicem je POZICE, ne tvar.
            key={`${String(index)}`}
            className={tile.lit === true ? 'rh-a2-parcels__tile rh-a2-parcels__tile--lit' : 'rh-a2-parcels__tile'}
            d={PARCEL_PATHS[tile.shape]}
            transform={`translate(${String(tile.x)} ${String(tile.y)}) rotate(${String(tile.rotate)}) scale(${String(tile.scale)})`}
          />
        ))}
      </svg>
    </div>
  );
}
